import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ListRow } from '@/components/ui/ListRow';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { runStateTone } from '@/components/runs/RunTranscript';
import { TIER_ORDER, TIER_META } from '@/components/agents/tiers';
import { useAsync } from '@/lib/useAsync';
import { timeAgo } from '@/lib/timeAgo';
import { api, ApiError, type ProjectDetail } from '@/lib/api';
import { useProjectCtx } from './ProjectLayout';

/** Runs tab: launch an agent on this project + the run history. */
export function ProjectRunsTab() {
  const { project } = useProjectCtx();
  const navigate = useNavigate();
  const runs = useAsync(() => api.listRuns(project.id), [project.id]);

  return (
    <div className="space-y-6">
      <RunLauncher project={project} onStarted={(runId) => navigate(runId)} />

      {runs.status === 'loading' && <SkeletonRows rows={4} />}
      {runs.status === 'error' && <ErrorState error={runs.error} onRetry={runs.reload} />}
      {runs.status === 'success' && runs.data.length === 0 && (
        <EmptyState icon={Play} title="No runs yet" description="Launch an agent above to start the first run." />
      )}
      {runs.status === 'success' && runs.data.length > 0 && (
        <ul className="divide-y divide-border">
          {runs.data.map((r) => (
            <ListRow
              key={r.id}
              title={r.title ?? r.agent?.name ?? 'Run'}
              meta={`${r.agent?.name ?? project.agents.find((a) => a.id === r.agentId)?.name ?? 'Agent'} · ${timeAgo(r.createdAt)}`}
              actions={<Badge tone={runStateTone(r.state)}>{r.state.toLowerCase()}</Badge>}
              onClick={() => navigate(r.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function RunLauncher({ project, onStarted }: { project: ProjectDetail; onStarted: (runId: string) => void }) {
  const [agentId, setAgentId] = useState(project.agents[0]?.id ?? '');
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const selected = project.agents.find((a) => a.id === agentId);

  async function launch(e: React.FormEvent) {
    e.preventDefault();
    if (!agentId) return;
    setBusy(true);
    try {
      const run = await api.startRun(project.id, {
        agentId,
        ...(input.trim() ? { input: input.trim() } : {}),
      });
      toast.success('Run started');
      onStarted(run.id);
    } catch (err) {
      toast.danger('Could not start run', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  }

  if (project.agents.length === 0) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-fg-muted">
            No agents are enabled on this project yet. Your WarriorLab team wires them up.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title="Run an agent" subtitle="Runs with this project's evidence and knowledge." />
      <CardBody>
        <form className="space-y-4" onSubmit={launch}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Agent" htmlFor="run-agent">
              <Select id="run-agent" value={agentId} onChange={(e) => setAgentId(e.target.value)}>
                {TIER_ORDER.map((tier) => {
                  const group = project.agents.filter((a) => a.kind === tier);
                  if (group.length === 0) return null;
                  return (
                    <optgroup key={tier} label={TIER_META[tier].plural}>
                      {group.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </Select>
            </Field>
            {selected?.description && (
              <p className="self-end pb-1.5 text-sm text-fg-muted">{selected.description}</p>
            )}
          </div>
          <Field label="Instructions" htmlFor="run-input" hint="Optional.">
            <textarea
              id="run-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={3}
              maxLength={4000}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              placeholder="Anything specific for this run"
            />
          </Field>
          <Button type="submit" disabled={busy || !agentId}>
            <Play className="h-4 w-4" /> {busy ? 'Starting…' : 'Run agent'}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
